import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, Mail, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../services/api';

interface LoginFormProps {
  onToggleMode: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onToggleMode }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [otpEnabled, setOtpEnabled] = useState<boolean | null>(null);

  const { login, refreshUser, user } = useAuth();

  // Check if OTP feature is enabled
  useEffect(() => {
    const checkOTPEnabled = async () => {
      try {
        const response = await authAPI.checkOTPEnabled();
        if (response.success) {
          setOtpEnabled(response.data.enabled);
        }
      } catch (error) {
        console.error('Failed to check OTP feature status:', error);
        // Default to false if check fails
        setOtpEnabled(false);
      }
    };
    checkOTPEnabled();
  }, []);

  // Check for OAuth errors in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthError = params.get('error');
    
    if (oauthError) {
      switch (oauthError) {
        case 'signups_disabled':
          setError(t('login.googleSignupDisabled'));
          break;
        case 'oauth_error':
          setError(t('login.googleSignInFailed'));
          break;
        case 'oauth_failed':
          setError(t('login.authenticationFailed'));
          break;
        case 'oauth_not_configured':
          setError(t('login.oauthNotConfigured'));
          break;
        default:
          setError(t('login.errorOccurred'));
      }
      
      // Clear the error from URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(formData.email, formData.password);
      // Refresh user to get latest data including phone verification status
      await refreshUser();
      
      // Get fresh user data - use context user if available, otherwise localStorage
      // Wait a bit for context to update after refreshUser
      await new Promise(resolve => setTimeout(resolve, 50));
      const currentUser = user || JSON.parse(localStorage.getItem('user') || '{}');
      
      // Check if phone verification is required - check feature flag first, then env var as fallback
      const requiresPhoneVerification = otpEnabled === true || (otpEnabled === null && process.env.REACT_APP_REQUIRE_PHONE_VERIFICATION === 'true');
      // If phone isn't verified, redirect to complete-profile page where user can verify their phone
      if (currentUser && !currentUser.phoneVerified && requiresPhoneVerification) {
        navigate('/complete-profile', { replace: true });
        return;
      }
      
      // Also check if phone is missing entirely - redirect to complete-profile
      if (currentUser && (!currentUser.phone || currentUser.phone.trim() === '')) {
        navigate('/complete-profile', { replace: true });
        return;
      }
      
      // Check provider document status (handled by ProtectedRoute, but we can navigate here too)
      if (currentUser?.role === 'provider') {
        const policeVerificationStatus = currentUser.policeVerificationStatus || 'not_submitted';
        const kycStatus = currentUser.kycStatus || 'not_submitted';
        const hasPoliceVerificationDocument = currentUser.policeVerificationDocument && 
          currentUser.policeVerificationDocument.trim() !== '';
        
        // Case 1: Police verification not submitted - redirect to complete profile
        if (policeVerificationStatus === 'not_submitted' && !hasPoliceVerificationDocument) {
          navigate('/complete-profile', { replace: true });
          return;
        }
        
        // Case 2: Police verification rejected - redirect to complete profile for re-upload
        if (policeVerificationStatus === 'rejected') {
          navigate('/complete-profile', { replace: true });
          return;
        }
        
        // Case 3: KYC not submitted - redirect to KYC (even if police verification is pending)
        // This allows user to upload both documents before showing pending verification
        if (kycStatus === 'not_submitted') {
          navigate('/kyc', { replace: true });
          return;
        }
        
        // Case 4: KYC rejected - redirect to KYC for re-upload
        if (kycStatus === 'rejected') {
          navigate('/kyc', { replace: true });
          return;
        }
        
        // Case 5: Both documents uploaded but pending/rejected - show review pending
        // Only show review pending if BOTH documents have been submitted (not just one)
        const bothDocumentsSubmitted = 
          (policeVerificationStatus !== 'not_submitted' || hasPoliceVerificationDocument) &&
          kycStatus !== 'not_submitted';
        
        if (bothDocumentsSubmitted && (policeVerificationStatus === 'pending' || kycStatus === 'pending' || 
            policeVerificationStatus === 'rejected' || kycStatus === 'rejected')) {
          navigate('/review-pending', { replace: true });
          return;
        }
      }
      
      // If no redirect happened above, navigate to home
      navigate('/', { replace: true });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-black">
            {t('login.signInToAccount')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {t('login.orCreateAccount')}{' '}
            <button
              onClick={onToggleMode}
              className="font-medium text-black hover:text-gray-600 underline"
            >
              {t('login.createNewAccount')}
            </button>
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
              {error}
            </div>
          )}
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                {t('login.emailAddress')}
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="appearance-none relative block w-full pl-10 pr-3 py-2 border border-gray-300 placeholder-gray-500 text-black bg-white rounded-md focus:outline-none focus:ring-gray-500 focus:border-gray-500 focus:z-10 sm:text-sm"
                  placeholder={t('login.enterEmail')}
                />
              </div>
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                {t('login.password')}
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="appearance-none relative block w-full pl-10 pr-10 py-2 border border-gray-300 placeholder-gray-500 text-black bg-white rounded-md focus:outline-none focus:ring-gray-500 focus:border-gray-500 focus:z-10 sm:text-sm"
                  placeholder={t('login.enterPassword')}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('login.signingIn') : t('login.signIn')}
            </button>
          </div>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">{t('login.orContinueWith')}</span>
              </div>
            </div>

            <div className="mt-6">
              <button
                type="button"
                onClick={() => window.location.href = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/auth/google`}
                className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                <span className="ml-2">{t('login.signInWithGoogle')}</span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginForm;
